import {
  Flex,
  Input,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import SingleDatePicker from "../../../shared/components/DatePicker/SingleDatePicker";
import { TimePicker } from "../../../shared/components/TimePicker/TimePicker";
import { answerAsString, isWeightAnswer, type WeightAnswer } from "./answerFormat";
import { EditorShell } from "./EditorShell";
import {
  isAnswerChanged,
  isNumberText,
  parseStoredDate,
  parseStoredTime,
  TIME_FORMAT,
  useEditorDraft,
} from "./editorValues";
import type { PatientDocumentAnswer, QuestionEditorProps } from "./types";

// Editors for textShort, textLong, number, date, time, bloodPressure and weight. Each saves the
// stored shape: strings for text/number, "yyyy-MM-dd" dates, "HH:mm" times (as the mobile
// <input type="date|time"> writes them), ["sys", "dia"] blood pressure, { amount, unit } weight.
// Save stays disabled until the reviewer changed the value (see editorValues.ts), and while a
// number / blood-pressure input holds partial input such as "5." (`isNumberText`).

function shellProps(
  props: QuestionEditorProps,
  params: { isTouched: boolean; isValid?: boolean; next: PatientDocumentAnswer }
) {
  return {
    canSave:
      params.isTouched && (params.isValid ?? true) && isAnswerChanged(props.value, params.next),
    error: props.error,
    isSaving: props.isSaving,
    label: props.question.label,
    onCancel: props.onCancel,
    onSave: () => props.onSave(params.next),
  };
}

function UnreadableNotice(props: { value: string | null }) {
  if (props.value === null) {
    return null;
  }
  return (
    <Text color="orange.600" fontSize="xs">
      The stored value &quot;{props.value}&quot; could not be read. It is kept unless you pick a new
      value.
    </Text>
  );
}

/** Blank (no value) or a number; partial input like "5." or "-" keeps Save disabled. */
function isBlankOrNumber(text: string): boolean {
  return text.trim() === "" || isNumberText(text);
}

function NotANumberNotice(props: { isShown: boolean }) {
  if (!props.isShown) {
    return null;
  }
  return (
    <Text color="red.600" fontSize="xs">
      Enter a number.
    </Text>
  );
}

function StringEditor(props: QuestionEditorProps) {
  const { draft, isTouched, updateDraft } = useEditorDraft(() => answerAsString(props.value) ?? "");
  const { itemType } = props.question;
  const isValid = itemType !== "number" || isBlankOrNumber(draft);

  return (
    <EditorShell {...shellProps(props, { isTouched, isValid, next: draft })}>
      {itemType === "textLong" ? (
        <Textarea
          autoFocus={true}
          rows={4}
          size="sm"
          value={draft}
          onChange={(event) => updateDraft(event.target.value)}
        />
      ) : itemType === "number" ? (
        <Stack spacing={1}>
          <NumberInput size="sm" value={draft} onChange={(value) => updateDraft(value)}>
            <NumberInputField autoFocus={true} />
          </NumberInput>
          <NotANumberNotice isShown={isTouched && !isValid} />
        </Stack>
      ) : (
        <Input
          autoFocus={true}
          size="sm"
          value={draft}
          onChange={(event) => updateDraft(event.target.value)}
        />
      )}
    </EditorShell>
  );
}

function DateEditor(props: QuestionEditorProps) {
  const stored = parseStoredDate(props.value);
  const { draft, isTouched, updateDraft } = useEditorDraft(() => stored.parsed);
  const next = draft === null ? null : draft.toString();

  return (
    <EditorShell {...shellProps(props, { isTouched, next })}>
      <Stack spacing={2}>
        <SingleDatePicker selected={draft} onChange={(value) => updateDraft(value)} />
        {!isTouched && <UnreadableNotice value={stored.unreadable} />}
      </Stack>
    </EditorShell>
  );
}

function TimeEditor(props: QuestionEditorProps) {
  const stored = parseStoredTime(props.value);
  const { draft, isTouched, updateDraft } = useEditorDraft(() => stored.parsed);
  const next = draft === null ? null : draft.format(TIME_FORMAT);

  return (
    <EditorShell {...shellProps(props, { isTouched, next })}>
      <Stack spacing={2}>
        <TimePicker value={draft ?? undefined} onChange={(value) => updateDraft(value)} />
        {!isTouched && <UnreadableNotice value={stored.unreadable} />}
      </Stack>
    </EditorShell>
  );
}

function BloodPressureEditor(props: QuestionEditorProps) {
  const { draft, isTouched, updateDraft } = useEditorDraft<[string, string]>(() => {
    const stored = Array.isArray(props.value) ? (props.value as unknown[]) : [];
    return [answerAsString(stored[0]) ?? "", answerAsString(stored[1]) ?? ""];
  });
  const next = draft[0] === "" && draft[1] === "" ? null : [draft[0], draft[1]];
  const isValid = isBlankOrNumber(draft[0]) && isBlankOrNumber(draft[1]);

  const setAt = (index: 0 | 1, value: string) => {
    updateDraft((prev) => (index === 0 ? [value, prev[1]] : [prev[0], value]));
  };

  return (
    <EditorShell {...shellProps(props, { isTouched, isValid, next })}>
      <Stack spacing={1}>
        <Flex alignItems="center" gap={2}>
          <NumberInput maxW="100px" size="sm" value={draft[0]} onChange={(v) => setAt(0, v)}>
            <NumberInputField autoFocus={true} placeholder="Systolic" />
          </NumberInput>
          <Text>/</Text>
          <NumberInput maxW="100px" size="sm" value={draft[1]} onChange={(v) => setAt(1, v)}>
            <NumberInputField placeholder="Diastolic" />
          </NumberInput>
          <Text color="gray.500" fontSize="sm">
            mmHg
          </Text>
        </Flex>
        <NotANumberNotice isShown={isTouched && !isValid} />
      </Stack>
    </EditorShell>
  );
}

const WEIGHT_UNITS: WeightAnswer["unit"][] = ["lbs", "KG"];

function WeightEditor(props: QuestionEditorProps) {
  const { draft, isTouched, updateDraft } = useEditorDraft(() => {
    const stored = isWeightAnswer(props.value) ? props.value : null;
    return {
      amount: stored === null ? "" : String(stored.amount),
      unit: stored?.unit ?? ("lbs" as WeightAnswer["unit"]),
    };
  });
  const parsed = parseFloat(draft.amount);
  const next: WeightAnswer | null = Number.isNaN(parsed)
    ? null
    : { amount: parsed, unit: draft.unit };

  return (
    <EditorShell {...shellProps(props, { isTouched, next })}>
      <Flex alignItems="center" gap={4}>
        <NumberInput
          maxW="120px"
          size="sm"
          value={draft.amount}
          onChange={(amount) => updateDraft((prev) => ({ ...prev, amount }))}
        >
          <NumberInputField autoFocus={true} placeholder="Amount" />
        </NumberInput>
        <RadioGroup
          value={draft.unit}
          onChange={(value) =>
            updateDraft((prev) => ({ ...prev, unit: value === "KG" ? "KG" : "lbs" }))
          }
        >
          <Stack direction="row" spacing={4}>
            {WEIGHT_UNITS.map((option) => (
              <Radio key={option} size="sm" value={option}>
                {option}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </Flex>
    </EditorShell>
  );
}

export function TextQuestionEditor(props: QuestionEditorProps) {
  switch (props.question.itemType) {
    case "date":
      return <DateEditor {...props} />;
    case "time":
      return <TimeEditor {...props} />;
    case "bloodPressure":
      return <BloodPressureEditor {...props} />;
    case "weight":
      return <WeightEditor {...props} />;
    default:
      return <StringEditor {...props} />;
  }
}
